<template>
  <div class="auth">
    <!-- left visual (desktop only) -->
    <section class="auth-visual">
      <div class="auth-visual-card">
        <div class="brand-row">
          <div class="brand-logo">BR</div>
          <div class="brand-title">
            <strong>Boro Credit</strong>
            <span>Secure credit panel</span>
          </div>
        </div>

        <div class="visual-copy">
          <h2>Welcome back.</h2>
          <p>
            Sign in to manage users, KYC requests, withdrawals, loans and employees.
          </p>
        </div>

        <div class="visual-stats">
          <div class="vstat">
            <i class="bx bx-shield-quarter"></i>
            <div>
              <strong>Secure</strong>
              <span>Role-based access</span>
            </div>
          </div>
          <div class="vstat">
            <i class="bx bx-time-five"></i>
            <div>
              <strong>Fast</strong>
              <span>Optimized admin flow</span>
            </div>
          </div>
          <div class="vstat">
            <i class="bx bx-check-circle"></i>
            <div>
              <strong>Reliable</strong>
              <span>Audit-ready actions</span>
            </div>
          </div>
        </div>

        <div class="auth-wave"></div>
      </div>
    </section>

    <!-- right form -->
    <section class="auth-form">
      <div class="auth-card" v-motion-fade>
        <div class="auth-head">
          <div class="chip">
            <i class="bx bx-lock-alt"></i>
            <span>Admin Login</span>
          </div>
          <h1>Sign in</h1>
          <p>Use your employee email and password to continue.</p>
        </div>

        <form class="form" @submit.prevent="onSubmit">
          <!-- email -->
          <div class="field">
            <label>Email</label>
            <div class="input">
              <i class="bx bx-envelope"></i>
              <input
                v-model.trim="form.email"
                type="email"
                autocomplete="email"
                placeholder="admin@company.com"
                required
              />
            </div>
            <small v-if="errors.email" class="err">{{ errors.email }}</small>
          </div>

          <!-- password -->
          <div class="field">
            <label>Password</label>
            <div class="input">
              <i class="bx bx-key"></i>
              <input
                v-model="form.password"
                :type="showPass ? 'text' : 'password'"
                autocomplete="current-password"
                placeholder="Enter password"
                required
              />
              <button
                class="eye"
                type="button"
                @click="showPass = !showPass"
                :aria-label="showPass ? 'Hide password' : 'Show password'"
              >
                <i class="bx" :class="showPass ? 'bx-show' : 'bx-hide'"></i>
              </button>
            </div>
            <small v-if="errors.password" class="err">{{ errors.password }}</small>
          </div>

          <!-- remember + forgot -->
          <div class="row">
            <label class="remember">
              <input v-model="form.remember" type="checkbox" />
              <span>Remember me</span>
            </label>

            <!-- <button type="button" class="link" @click="onForgot">
              Forgot password?
            </button> -->
          </div>

          <!-- button -->
          <button class="btn-login" type="submit" :disabled="loading">
            <span v-if="!loading">
              <i class="bx bx-log-in"></i>
              Sign In
            </span>

            <span v-else class="loading">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </span>
          </button>

          <!-- helper -->
          <div class="hint">
            <i class="bx bx-info-circle"></i>
            <span>
              For security, login attempts are monitored. Contact Boro Credit if locked.
            </span>
          </div>
        </form>

        <div class="auth-foot">
          <span>© {{ new Date().getFullYear() }} Boro Credit</span>
          <span class="sep">•</span>
          <span class="muted">v1.0</span>
        </div>
      </div>
    </section>
  </div>
</template>
<script>
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { adminLogin } from "@/apis/adminauthcon";

export default {
  name: "LoginPage",

  data() {
    return {
      router: null,
      toast: null,

      showPass: false,
      loading: false,

      form: {
        email: "",
        password: "",
        remember: true,
      },

      errors: {
        email: "",
        password: "",
      },
    };
  },

  methods: {
    validate() {
      this.errors.email = "";
      this.errors.password = "";

      if (!this.form.email || !this.form.email.includes("@")) {
        this.errors.email = "Please enter a valid email address.";
      }
      if (!this.form.password || this.form.password.length < 6) {
        this.errors.password = "Password must be at least 6 characters.";
      }

      return !this.errors.email && !this.errors.password;
    },

    onForgot() {
      this.toast.info("Forgot password flow will be added (email reset).");
    },

    async onSubmit() {
      if (!this.validate()) return;

      this.loading = true;

      try {
        const res = await adminLogin(this.form.email, this.form.password);

        if (!res || res.status !== true) {
          this.toast.error(res?.success || "Login failed. Please try again.");
          return;
        }

        this.toast.success(res?.success || "Login successful ✅");

        // ✅ store admin session
        const udt = res?.user_dt || {};
        sessionStorage.setItem(
          "ldata",
          JSON.stringify({
            ...udt,
            type: "admin",
            remember: this.form.remember ? "1" : "0",
          })
        );

        // ✅ Redirect dashboard
        this.router.push("/");
        window.location.reload();
      } catch (e) {
        this.toast.error("Login failed. Please try again.");
      } finally {
        this.loading = false;
      }
    },
  },

  mounted() {
    this.router = useRouter();
    this.toast = useToast();

    // ✅ if already logged in, go dashboard
    try {
      const ldata = sessionStorage.getItem("ldata");
      if (ldata) {
        const dt = JSON.parse(ldata);
        if (dt && dt.type === "admin" && dt.user_id) {
          sessionStorage.setItem("ldata",JSON.stringify(dt.user_dt));
          this.router.push("/");
        }
      }
    } catch (e) {
      console.log("sfds");
    }
  },
};
</script>

<style scoped>
/* ✅ remove any default margin/padding and force full viewport */
.auth {
  width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 0;

  display: grid;
  grid-template-columns: 1fr 1fr;
  overflow: hidden;
  background: #ffffff;
}

/* left side full height */
.auth-visual {
  height: 100%;
  width: 100%;
  padding: 0;
  margin: 0;
  display: flex;
}

/* right side full height */
.auth-form {
  height: 100%;
  width: 100%;
  padding: 0;
  margin: 0;

  display: flex;
  align-items: center;
  justify-content: center;
}

/* ✅ remove card spacing so it touches full side
   (If you still want card style inside, keep padding here) */
.auth-visual-card {
  width: 100%;
  height: 100%;
  border-radius: 0;
  padding: 40px;
}

/* ✅ If you want right side FULL without card padding */
.auth-card {
  width: 100%;
  height: 100%;
  border-radius: 0;
  padding: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* ✅ Mobile: hide left side, right becomes full screen */
@media (max-width: 980px) {
  .auth {
    grid-template-columns: 1fr;
  }
  .auth-visual {
    display: none;
  }
  .auth-form {
    width: 100%;
    height: 100%;
  }
}
/* ===========================================
   ✅ MOBILE PATCH (DO NOT AFFECT DESKTOP)
   Paste at END of your style scoped
   =========================================== */

@media (max-width: 980px) {
  /* keep your current hide-left behavior */
  .auth {
    grid-template-columns: 1fr !important;
    height: auto !important;
    min-height: 100vh !important;
    overflow: visible !important;
    background: #f6f8fc !important;
  }

  .auth-visual {
    display: none !important;
  }

  /* give breathing space on mobile */
  .auth-form {
    padding: 18px !important;
    align-items: center !important;
    justify-content: center !important;
  }

  /* turn full height screen into card */
  .auth-card {
    height: auto !important;
    min-height: auto !important;
    max-width: 520px !important;
    width: 100% !important;

    padding: 22px 18px !important;
    border-radius: 26px !important;

    background: rgba(255, 255, 255, 0.96) !important;
    border: 1px solid #e7eef8 !important;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06) !important;
  }

  /* typography scale */
  .auth-head h1 {
    font-size: 26px !important;
    letter-spacing: -0.4px !important;
  }

  .auth-head p {
    font-size: 13px !important;
  }

  /* fix remember row wrap */
  .row {
    flex-wrap: wrap !important;
    gap: 10px !important;
  }

  /* comfortable input/button size */
  .input input {
    height: 50px !important;
    font-size: 14px !important;
    border-radius: 18px !important;
  }

  .btn-login {
    height: 52px !important;
    border-radius: 18px !important;
  }

  /* footer spacing */
  .auth-foot {
    margin-top: 18px !important;
    justify-content: center !important;
    flex-wrap: wrap !important;
    gap: 8px !important;
  }
}

/* ✅ extra tiny phones */
@media (max-width: 420px) {
  .auth-form {
    padding: 14px !important;
  }

  .auth-card {
    padding: 18px 14px !important;
    border-radius: 24px !important;
  }

  .input input {
    height: 48px !important;
    font-size: 13px !important;
  }
}

</style>
